import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { Dayjs } from 'dayjs';

export type InvoiceMonthlyDetailDateFilterValue = {
	invoiceStartDate: Dayjs | null;
	invoiceEndDate: Dayjs | null;
	parkingPeriodStartMonth: number | '';
	parkingPeriodStartYear: number | '';
	parkingPeriodEndMonth: number | '';
	parkingPeriodEndYear: number | '';
};

type Props = {
	value: InvoiceMonthlyDetailDateFilterValue;
	onChange: (value: InvoiceMonthlyDetailDateFilterValue) => void;
};

const monthOptions = [
	{ label: 'January', value: 0 },
	{ label: 'February', value: 1 },
	{ label: 'March', value: 2 },
	{ label: 'April', value: 3 },
	{ label: 'May', value: 4 },
	{ label: 'June', value: 5 },
	{ label: 'July', value: 6 },
	{ label: 'August', value: 7 },
	{ label: 'September', value: 8 },
	{ label: 'October', value: 9 },
	{ label: 'November', value: 10 },
	{ label: 'December', value: 11 },
];

const getYearOptions = () => {
	const startYear = 2021;
	const endYear = new Date().getFullYear() + 1;
	const yearOptions = [];

	for (let year = startYear; year <= endYear; year++) {
		yearOptions.push(year);
	}

	return yearOptions;
};

export const InvoiceMonthlyDetailDateFilter = ({ value, onChange }: Props) => {
	const updateValue = (changes: Partial<InvoiceMonthlyDetailDateFilterValue>) => {
		onChange({
			...value,
			...changes,
		});
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Stack spacing={2}>
				<Typography variant='subtitle1' fontWeight={600}>
					Invoice and parking period dates
				</Typography>

				<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
					<DatePicker
						label='Invoice start date'
						value={value.invoiceStartDate}
						onChange={(date) => updateValue({ invoiceStartDate: date })}
						slotProps={{ textField: { fullWidth: true } }}
					/>

					<DatePicker
						label='Invoice end date'
						value={value.invoiceEndDate}
						onChange={(date) => updateValue({ invoiceEndDate: date })}
						slotProps={{ textField: { fullWidth: true } }}
					/>
				</Stack>

				<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
					<FormControl fullWidth>
						<InputLabel id='parking-period-start-month-label'>Parking period start month</InputLabel>
						<Select
							labelId='parking-period-start-month-label'
							label='Parking period start month'
							value={value.parkingPeriodStartMonth}
							onChange={(event) =>
								updateValue({
									parkingPeriodStartMonth: event.target.value as number | '',
								})
							}
						>
							<MenuItem value=''>
								<em>None</em>
							</MenuItem>
							{monthOptions.map((month) => (
								<MenuItem key={month.value} value={month.value}>
									{month.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl fullWidth>
						<InputLabel id='parking-period-start-year-label'>Parking period start year</InputLabel>
						<Select
							labelId='parking-period-start-year-label'
							label='Parking period start year'
							value={value.parkingPeriodStartYear}
							onChange={(event) =>
								updateValue({
									parkingPeriodStartYear: event.target.value as number | '',
								})
							}
						>
							<MenuItem value=''>
								<em>None</em>
							</MenuItem>
							{getYearOptions().map((year) => (
								<MenuItem key={year} value={year}>
									{year}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Stack>

				<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
					<FormControl fullWidth>
						<InputLabel id='parking-period-end-month-label'>Parking period end month</InputLabel>
						<Select
							labelId='parking-period-end-month-label'
							label='Parking period end month'
							value={value.parkingPeriodEndMonth}
							onChange={(event) =>
								updateValue({
									parkingPeriodEndMonth: event.target.value as number | '',
								})
							}
						>
							<MenuItem value=''>
								<em>None</em>
							</MenuItem>
							{monthOptions.map((month) => (
								<MenuItem key={month.value} value={month.value}>
									{month.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl fullWidth>
						<InputLabel id='parking-period-end-year-label'>Parking period end year</InputLabel>
						<Select
							labelId='parking-period-end-year-label'
							label='Parking period end year'
							value={value.parkingPeriodEndYear}
							onChange={(event) =>
								updateValue({
									parkingPeriodEndYear: event.target.value as number | '',
								})
							}
						>
							<MenuItem value=''>
								<em>None</em>
							</MenuItem>
							{getYearOptions().map((year) => (
								<MenuItem key={year} value={year}>
									{year}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Stack>
			</Stack>
		</LocalizationProvider>
	);
};
